import { defineNuxtPlugin } from '#app'
import { useSettingsStore } from '~/stores/common/settings'
import { useNotificationsStore } from '~/stores/common/notifications'
import { useGuideStore } from '@/stores/common/guide.js'
import {
  config as guideConfig,
  options as guideOptions
} from '@/_plugins/guide/configs/app'
import { useAccountStore } from '@/stores/account'
import number from '@/utils/number'
import { watch, computed } from 'vue'
import { useApiClient } from '@/composables/api-client'

const NODE_ENV = process.env.NODE_ENV
const isDev = NODE_ENV === 'development'

export default defineNuxtPlugin(async nuxtApp => {
  const { $snacks, $time, $i18n, $dialog, $analytics, $guide, $sdk } = nuxtApp

  const settingsStore = useSettingsStore()

  // На маршрутах с { ssr: false } - settings из app.server.js не попадают в стор, нужен запрос на клиенте
  if (!settingsStore.data) {
    await settingsStore.fetchData()
  }

  const notificationsStore = useNotificationsStore()
  const guideStore = useGuideStore()
  const accountStore = useAccountStore()
  const apiClient = await useApiClient()
  const isAuthorized = computed(() => accountStore.isAuthorized)
  const account = computed(() => accountStore.account)

  let alreadyProcessingError = false

  const authorization = async ({ options }) => {
    const token = await $sdk
      .module('auth')
      .then(({ getAuthorizationToken }) => getAuthorizationToken())

    if (token) {
      options.headers.Authorization = token
    }
  }

  apiClient.setAuthorization(authorization)

  nuxtApp.hook('api-client:error', error => {
    const method = error?.options?.method?.toUpperCase()
    if (['POST', 'PUT', 'DELETE'].includes(method)) {
      const data = error.response?._data
      const message = data?.message || data?.code || $i18n.t('error')
      $snacks.error(message)
    }
    if (
      error.response.status.toString() === '401' &&
      !alreadyProcessingError &&
      location.pathname !== '/' &&
      !config.public.isMaintenance
    ) {
      alreadyProcessingError = true
      showSignIn()
        .then(() => {
          location.reload()
        })
        .catch(() => {
          alreadyProcessingError = false
          $sdk.module('auth').then(({ logout }) => logout())
        })
    }
  })

  settingsStore.fetchState().then(({ time }) => {
    $time.setTimestamp(time * 1000)
  })

  function showGuide() {
    if (
      !$guide.active &&
      window.innerWidth > 570 &&
      !guideStore.guideIsFinished('welcome')
    ) {
      guideStore.setLocalStateAsFinished('welcome')
      return $guide.create(guideConfig, guideOptions)
    }
  }

  function showSignUp() {
    $dialog({
      content: defineAsyncComponent(
        () => import('~/components/account/sign-up.vue')
      ),
      wrapper: null,
      closeOnEsc: false,
      closeByOutsideClick: false,
      closeOnChangeRoute: false,
      onClose: async () => {
        await showGuide()
      }
    })
  }

  function showSignIn() {
    return new Promise((resolve, reject) => {
      $dialog({
        content: defineAsyncComponent(
          () => import('~/components/account/sign-in.vue')
        ),
        wrapper: null,
        fetchData: () =>
          $sdk.module('auth').then(({ fetchClients }) => fetchClients()),
        onClose: e => {
          if (e === true) {
            resolve()
          } else {
            reject(new Error('Unauthorized'))
          }
        }
      })
    })
  }

  nuxtApp.hook('app:mounted', async () => {
    if (isDev) {
      const config = useRuntimeConfig()
      document.body.setAttribute('data-dev-env', config.public.appEnv)
      document.body.classList.add('dev')
    }

    watch(
      isAuthorized,
      async value => {
        if (value) {
          if (account.value?.profile && !account.value.profile.agree) {
            showSignUp()
          }

          // Temporary off
          // notificationsStore.fetchUnreadCount()

          await Promise.allSettled([
            accountStore.fetchPrivilegeKeys(),
            $sdk
              .module('common')
              .then(({ getWSInstance }) => getWSInstance().authorize())
          ])
        } else {
          await $sdk
            .module('common')
            .then(({ getWSInstance }) => getWSInstance().logout())
        }
      },
      {
        immediate: true
      }
    )

    $sdk.module('common').then(({ getWSInstance }) => {
      const ws = getWSInstance()

      ws.addEventListener(
        'coinTransaction',
        ({ direction, amount, code, balance }) => {
          const message = `${number(amount)} ${code.toUpperCase()}`
          accountStore.setBalance(code, balance)
          if (direction === 'increase') {
            $snacks.increase(`+${message}`, { duration: 6000 })
          } else if (direction === 'decrease') {
            $snacks.decrease(message, { duration: 6000 })
          }
        }
      )

      ws.addEventListener(['notification'], ({ text }) => {
        notificationsStore.incUnread()
        $snacks.info(text, { duration: 5000 })
      })

      ws.connect()
    })

    $sdk.module('auth').then(({ getAccount }) => getAccount())

    nuxtApp.provide('appClient', {
      showSignIn
    })
  })
})
